import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { Button } from "@typedoc/ui";
import { CreateOrganisation } from "../../components/onboarding/create-organisation";
import { JoinOrganisation } from "../../components/onboarding/join-organisation";
import { ErrorNotice } from "../../components/error-notice";
import { MainLandmark } from "../../components/main-landmark";
import { PendingScreen } from "../../components/pending-screen";
import { normalizeAppError } from "../../lib/app-error";
import { queryClient } from "../../lib/query-client";
import { onboardingStateQueryOptions } from "../../lib/queries/onboarding";

const pending = <PendingScreen label="Getting your workspace ready…" />;

export const Route = createFileRoute("/_authenticated/onboarding")({
  // Someone who has finished goes home before this screen is drawn, not after.
  beforeLoad: async ({ context }) => {
    const onboarding = await queryClient
      .ensureQueryData(onboardingStateQueryOptions(context.session.user.id))
      .catch(() => null);
    if (onboarding?.state === "done") throw redirect({ to: "/home", replace: true });
  },
  pendingComponent: () => pending,
  component: Onboarding,
});

/**
 * Renders whichever of the three states the server resolved — never decides that itself. Tasks 2
 * and 3 fill in "create", "join" and "requested"; this shell only resolves the state, and leaves
 * once it is "done".
 */
function Onboarding() {
  const navigate = useNavigate();
  // The state names another firm when it names one at all, so it is cached per user. The session
  // is already settled: `_authenticated` renders nothing below it until it is.
  const { session } = Route.useRouteContext();
  const { data, isPending, error, refetch } = useQuery(
    onboardingStateQueryOptions(session.user.id),
  );
  const done = data?.state === "done";

  useEffect(() => {
    if (done) void navigate({ to: "/home", replace: true });
  }, [done, navigate]);

  return (
    <MainLandmark>
      <section className="page onboarding-page">
        {isPending || done ? (
          pending
        ) : error ? (
          <div className="onboarding-status">
            <ErrorNotice error={normalizeAppError(error)} variant="destructive" />
            <Button variant="outline" onClick={() => void refetch()}>
              Try again
            </Button>
          </div>
        ) : data?.state === "create" ? (
          <CreateOrganisation domain={data.domain} />
        ) : data?.state === "join" || data?.state === "requested" ? (
          <JoinOrganisation state={data} />
        ) : null}
      </section>
    </MainLandmark>
  );
}
